import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import GoBackButton from "@/common/components/GoBackButton";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import LogRequestsTable from "@/common/components/LogRequestsTable";
import Select from "@/common/components/Select";
import Tabs from "@/common/components/Tabs";
import TextField from "@/common/components/TextField";
import { UnlockUserAccess } from "@/common/components/UnlockUserAccess";
import { userStatusTranslation } from "@/common/constants/userStatus.constant";
import { useEstablishmentUser } from "@/common/hooks/queries/useEstablishmentUser";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { dateTransformer } from "@/common/utils/dateTransformer";
import { formatCpf, formatPhoneNumber } from "@/common/utils/formatters";
import RequestPasswordReset from "@/features/establishments/components/RequestPasswordReset";
import { ClientUserConsumptionHistory } from "@/features/establishments/components/showUser/consumptionHistory";
import EstablishmentUserTokens from "@/features/establishments/components/showUser/establishmentUserTokens";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsRightIcon, UserRoundIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";

const Show = () => {
  const { establishmentId, id } = useParams();
  const queryClient = useQueryClient();

  const { hasUserPermission } = useUserPermissions();

  const { data: establishment } = useEstablishmentCompany(establishmentId || '');

  const {
    data: establishmentUser,
    isPending: establishmentUserIsLoading,
    error: establishmentUserError = {} as any,
    refetch: refetchEstablishmentUser,
  } = useEstablishmentUser({ id: id || '' });

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={establishmentUserError?.status}
      refetchFunction={refetchEstablishmentUser}
      notFoundFallback={`/establishments/${establishmentId}/users`}
      notFoundButtonText={'Ir para Usuários'}
    >
      <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <GoBackButton
              fallback={`/establishments/${establishmentId}/users`}
              searchLastRouteFromReference={`/establishments/${establishmentId}/users/${id}`}
            />
            <p className="text-white-400 font-normal">{establishment?.general_information?.fantasy_name}</p>
            <ChevronsRightIcon color='#ffffffb3' />
            <h2>{establishmentUser?.full_name}</h2>
          </div>
          {!establishmentUserIsLoading && (
            <div className="flex gap-4">
              <Link to={`/establishments/${establishmentId}/users/${id}/edit`} data-testid="access_establishment_user_update_button">
                <Button className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">Editar</Button>
              </Link>
            </div>
          )}
        </div>
        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          tabsList={[
            {
              label: 'Dados',
              value: '',
              body: (
                <>
                  <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-192px)]">
                    {!!establishmentUser?.failed_attempts && establishmentUser?.failed_attempts > 0 && (
                      <UnlockUserAccess
                        userId={id || ''}
                        failedAttempts={establishmentUser?.failed_attempts}
                        lockedAt={establishmentUser?.locked_at}
                        entity="establishment"
                        operationKlassToCheckPermission="Admin::Establishment::Users::UnlockUserAccess"
                        onSuccess={() => {
                          queryClient.invalidateQueries({ queryKey: ['establishmentUser', id] })
                        }}
                      />
                    )}

                    <p className="text-lg font-bold">Dados pessoais</p>
                    <div className="flex flex-col gap-2">
                      <p className="text-xs">Foto</p>
                      <ImageWithPlaceholder
                        url={establishmentUser?.profile_picture?.url}
                        alt={establishmentUser?.full_name}
                        className="size-20"
                        iconClassName="size-16"
                        placeholderIcon={UserRoundIcon}
                      />
                    </div>
                    <div className="flex gap-2 w-full justify-between">
                      <TextField
                        disabled
                        label="CPF"
                        className="w-3/4"
                        value={establishmentUser?.cpf ? formatCpf(establishmentUser.cpf) : ''}
                        data-testid="cpf"
                      />
                      <TextField
                        disabled
                        label="Data de nascimento"
                        className="w-1/4"
                        value={establishmentUser?.date_of_birth ? dateTransformer(establishmentUser?.date_of_birth) : ''}
                        data-testid="date_of_birth"
                      />
                    </div>
                    <div className="flex gap-2 w-full justify-between">
                      <TextField
                        disabled
                        label="Nome"
                        className="w-3/4"
                        value={establishmentUser?.full_name || ''}
                        data-testid="full_name"
                      />
                      <Select
                        label="Perfil"
                        className="w-1/4"
                        value={establishmentUser?.user_roles?.map(({ role }) => ({
                          label: role.display_name,
                          value: role.id,
                        }))}
                        isMulti
                        isDisabled
                        data-testid="roles"
                      />
                    </div>
                    <TextField
                      disabled
                      label="Status"
                      value={userStatusTranslation[(establishmentUser?.status || 'default') as keyof typeof userStatusTranslation]}
                      data-testid="status"
                    />
                    <TextField
                      disabled
                      label="Telefone"
                      value={establishmentUser?.phone?.number ? formatPhoneNumber(establishmentUser.phone.number) : ''}
                      data-testid="phone_number"
                    />
                    <TextField
                      disabled
                      label="Email"
                      value={establishmentUser?.email || ''}
                      data-testid="email"
                    />

                    {establishmentUser && hasUserPermission('Admin::Establishment::Users::RequestPasswordReset') && (
                      <div className="w-full rounded-sm p-4 bg-white-100">
                        <div className="flex w-full items-center justify-between">
                          <p>Senha de acesso ao sistema de gestão TCard</p>

                          <RequestPasswordReset id={id!} />
                        </div>
                      </div>
                    )}

                    <EstablishmentUserTokens />
                  </Box>
                </>
              )
            },
            {
              label: 'Histórico de vendas',
              value: 'consumptionHistory',
              body: (
                <ClientUserConsumptionHistory />
              )
            },
            {
              label: 'Histórico de ações',
              value: 'actionsHistory',
              body: (
                <LogRequestsTable type="EstablishmentUser" id={id} />
              )
            },
          ]}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default Show;
